/** At-a-glance operational state; technical sync diagnostics stay in the card below. */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { ShopSummary } from '../../api/types.ts';
import { palette } from '../../theme.ts';
import { formatLocalDateTime, syncBadgeMeta } from '../dashboard/dashboardHelpers.ts';
import { enrollmentHealth, licenceHealth, resolvedSyncFreshness, type HealthMeta } from './shopHealthHelpers.ts';
import { ShopStatusControl } from './ShopStatusControl.tsx';

function HealthItem({ label, meta, detail }: Readonly<{ label: string; meta: HealthMeta; detail?: string }>) {
  return (
    <Box sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 150 }, flex: '1 1 150px' }}>
      <Typography variant="caption" sx={{ color: palette.textSecondary, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Chip
        size="small"
        label={meta.label}
        color={meta.color === 'default' ? undefined : meta.color}
        variant="outlined"
      />
      {detail && (
        <Typography variant="caption" sx={{ color: palette.textSecondary, display: 'block', mt: 0.5 }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
}

export function ShopHealthStrip({
  shop,
  canWrite,
  onStatusChanged,
}: Readonly<{ shop: ShopSummary; canWrite: boolean; onStatusChanged: () => void }>): React.JSX.Element {
  const sync = syncBadgeMeta(resolvedSyncFreshness(shop));
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Operational health
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <ShopStatusControl shop={shop} canWrite={canWrite} onChanged={onStatusChanged} />
        <HealthItem
          label="Report sync"
          meta={sync}
          detail={shop.last_sync_at ? `Last report ${formatLocalDateTime(shop.last_sync_at)}` : 'No report received'}
        />
        <HealthItem label="Current licence" meta={licenceHealth(shop.last_licence_expiry)} />
        <HealthItem label="Registration" meta={enrollmentHealth(shop)} />
      </Box>
    </Paper>
  );
}

export default ShopHealthStrip;
